import {
  DateFilterModel,
  FilterModel,
  ICombinedSimpleModel,
  IServerSideGetRowsRequest,
  SetFilterModel,
  TextFilterModel,
} from 'ag-grid-enterprise';
import { FilterParameterOperator } from '@app/shared/search/interfaces/filter-parameter';
import { SearchRequestBuilder } from '@app/shared/search/builders/search-request.builder';
import {
  ComplexSearchRequest,
  SearchOperand,
  SearchRequest,
} from '../interfaces/search-request';

export class ComplexSearchRequestBuilder<T> {
  private readonly complexSearchRequest: ComplexSearchRequest;

  constructor() {
    this.complexSearchRequest = {
      filters: [],
      optionalFilters: [],
    };
  }

  addSearchRequest(
    searchRequest: SearchRequest,
  ): ComplexSearchRequestBuilder<T> {
    if (searchRequest) {
      this.complexSearchRequest.filters.push(searchRequest);
    }
    return this;
  }

  addSearchRequests(
    searchRequests: SearchRequest[],
  ): ComplexSearchRequestBuilder<T> {
    this.complexSearchRequest.filters.push(...searchRequests);
    return this;
  }

  combine(
    complexSearchRequest: ComplexSearchRequest,
  ): ComplexSearchRequestBuilder<T> {
    if (complexSearchRequest) {
      this.addSearchRequests(complexSearchRequest.filters);
      this.addOptionalSearchRequests(complexSearchRequest.optionalFilters);
    }
    return this;
  }

  addOptionalSearchRequest(
    searchRequest: SearchRequest,
  ): ComplexSearchRequestBuilder<T> {
    if (searchRequest) {
      this.complexSearchRequest.optionalFilters.push(searchRequest);
    }
    return this;
  }

  addOptionalSearchRequests(
    searchRequests: SearchRequest[],
  ): ComplexSearchRequestBuilder<T> {
    this.complexSearchRequest.optionalFilters.push(...searchRequests);
    return this;
  }

  addAgGridSetFilter(
    columnName: keyof T,
    request: IServerSideGetRowsRequest,
    emptyArrayValue: any,
  ) {
    const filterModel: FilterModel = request.filterModel;
    this.addAgGridSetFilterFromFilterModel(
      columnName,
      filterModel,
      emptyArrayValue,
    );
  }

  addAgGridTextFilters(
    columnNames: (keyof T)[],
    request: IServerSideGetRowsRequest,
  ) {
    const filterModel: FilterModel = request.filterModel;
    for (const columnName of columnNames) {
      this.addAgGridTextFilterFromFilterModel(columnName, filterModel);
    }
  }

  addAgGridDateFilter(columnName: keyof T, request: IServerSideGetRowsRequest) {
    const filterModel: FilterModel = request.filterModel;
    let filter: DateFilterModel | ICombinedSimpleModel<DateFilterModel> =
      filterModel[columnName.toString()];
    if (filter) {
      if (Object.prototype.hasOwnProperty.call(filter, 'conditions')) {
        filter = filter as ICombinedSimpleModel<DateFilterModel>;
        const searchRequests = filter.conditions.map((dateFilter) =>
          this.addAgGridDateFilterFromFilterModel(columnName, dateFilter),
        );
        if (filter.operator === 'OR') {
          this.addOptionalSearchRequests(searchRequests);
        } else {
          this.addSearchRequests(searchRequests);
        }
      } else {
        filter = filter as DateFilterModel;
        const searchRequest = this.addAgGridDateFilterFromFilterModel(
          columnName,
          filter,
        );
        this.addSearchRequest(searchRequest);
      }
    }
  }

  build(): ComplexSearchRequest {
    return this.complexSearchRequest;
  }

  private addAgGridTextFilterFromFilterModel(
    columnName: keyof T,
    filterModel: FilterModel,
  ) {
    const filter: TextFilterModel = filterModel[columnName.toString()];
    if (filter) {
      const searchRequestBuilder = new SearchRequestBuilder<T>().setOperand(
        SearchOperand.AND,
      );
      const type = filter.type;
      const value = filter.filter;
      let filterOperator: FilterParameterOperator;
      if (filter.type == 'inRange') {
        searchRequestBuilder.addNewFilterParameter(
          columnName,
          filter.filter,
          FilterParameterOperator.GreaterThanOrEqual,
        );
        searchRequestBuilder.addNewFilterParameter(
          columnName,
          filter.filterTo,
          FilterParameterOperator.LessThanOrEqual,
        );
      } else {
        switch (type) {
          case 'equals':
            filterOperator = FilterParameterOperator.Equals;
            break;
          case 'contains':
            filterOperator = FilterParameterOperator.Contains;
            break;
          case 'startsWith':
            filterOperator = FilterParameterOperator.StartsWith;
            break;
          case 'endsWith':
            filterOperator = FilterParameterOperator.EndsWith;
            break;
          case 'notEqual':
            filterOperator = FilterParameterOperator.NotEqual;
            break;
          case 'greaterThan':
            filterOperator = FilterParameterOperator.GreaterThan;
            break;
          case 'greaterThanOrEqual':
            filterOperator = FilterParameterOperator.GreaterThanOrEqual;
            break;
          case 'lessThan':
            filterOperator = FilterParameterOperator.LessThan;
            break;
          case 'lessThanOrEqual':
            filterOperator = FilterParameterOperator.LessThanOrEqual;
            break;
        }

        searchRequestBuilder.addNewFilterParameter(
          columnName,
          value,
          filterOperator,
        );
      }
      this.addSearchRequest(searchRequestBuilder.build());
    }
  }

  private addAgGridSetFilterFromFilterModel(
    columnName: keyof T,
    filterModel: FilterModel,
    emptyArrayValue: any,
  ) {
    const filter: SetFilterModel = filterModel[columnName.toString()];
    if (filter && filter.values) {
      const values: string[] = filter.values;
      const searchRequestBuilder = new SearchRequestBuilder<T>().setOperand(
        SearchOperand.OR,
      );
      values.forEach((value) => {
        searchRequestBuilder.addNewFilterParameter(
          columnName,
          value,
          FilterParameterOperator.Equals,
        );
      });
      if (values.length < 1) {
        searchRequestBuilder.addNewFilterParameter(
          columnName,
          emptyArrayValue,
          FilterParameterOperator.Equals,
        );
      }
      this.addSearchRequest(searchRequestBuilder.build());
    }
  }

  private addAgGridDateFilterFromFilterModel(
    columnName: keyof T,
    filter: DateFilterModel,
  ): SearchRequest {
    const searchRequestBuilder = new SearchRequestBuilder<T>().setOperand(
      SearchOperand.OR,
    );
    const dateFrom = filter.dateFrom;
    const dateTo = filter.dateTo;
    if (filter.type === 'inRange') {
      searchRequestBuilder.setOperand(SearchOperand.AND);
      searchRequestBuilder.addNewFilterParameter(
        columnName,
        dateFrom,
        FilterParameterOperator.GreaterThan,
      );
      searchRequestBuilder.addNewFilterParameter(
        columnName,
        dateTo,
        FilterParameterOperator.LessThan,
      );
    } else {
      let filterOperator: FilterParameterOperator;
      switch (filter.type) {
        case 'equals':
          filterOperator = FilterParameterOperator.Contains;
          break;
        case 'notEqual':
          filterOperator = FilterParameterOperator.NotEqual;
          break;
        case 'lessThan':
          filterOperator = FilterParameterOperator.LessThan;
          break;
        case 'lessThanOrEqual':
          filterOperator = FilterParameterOperator.LessThanOrEqual;
          break;
        case 'greaterThan':
          filterOperator = FilterParameterOperator.GreaterThan;
          break;
        case 'greaterThanOrEqual':
          filterOperator = FilterParameterOperator.GreaterThanOrEqual;
          break;
      }

      searchRequestBuilder.addNewFilterParameter(
        columnName,
        dateFrom,
        filterOperator,
      );
    }
    return searchRequestBuilder.build();
  }
}
